import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon, DollarSign, MapPin, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

interface PricingSettings {
  siding_price_per_sqft_vinyl: number;
  siding_price_per_sqft_fiber: number;
  soffit_price_per_sqft: number;
  fascia_price_per_ft: number;
  overhead_percent: number;
  profit_margin_percent: number;
}

interface BuildingData {
  facadeAreaSqFt?: number;
  soffitAreaSqFt?: number;
  fasciaLengthFt?: number;
}

interface SidingEstimateProps {
  buildingData?: BuildingData | null;
}

export function SidingEstimate({ buildingData }: SidingEstimateProps) {
  const [settings, setSettings] = useState<PricingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [sidingArea, setSidingArea] = useState<string>("");
  const [soffitArea, setSoffitArea] = useState<string>("");
  const [fasciaLength, setFasciaLength] = useState<string>("");
  const [material, setMaterial] = useState<"vinyl" | "fiber">("vinyl");
  const [measuring, setMeasuring] = useState(false);
  const [autoMeasured, setAutoMeasured] = useState(false);
  
  const { user } = useAuth();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      if (!user) return;

      const { data } = await supabase
        .from('pricing_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (data) {
        setSettings(data as PricingSettings);
      }
    } catch (error) {
      console.error('Error loading pricing settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoMeasureFromMap = () => {
    if (!buildingData) {
      toast.error("No building measurement data available. Please run a measurement first.");
      return;
    }

    setMeasuring(true);
    
    // Use Solar API building data
    if (buildingData.facadeAreaSqFt) {
      setSidingArea(String(buildingData.facadeAreaSqFt));
    }
    
    if (buildingData.soffitAreaSqFt) {
      setSoffitArea(String(buildingData.soffitAreaSqFt));
    }
    
    if (buildingData.fasciaLengthFt) {
      setFasciaLength(String(buildingData.fasciaLengthFt));
    }
    
    setMeasuring(false);
    setAutoMeasured(true);
    toast.success("Siding measurements loaded from Solar API!");
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Loading pricing...</p>
        </CardContent>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert>
            <InfoIcon className="h-4 w-4" />
            <AlertDescription>
              Please set your pricing rates in{" "}
              <Link to="/settings?tab=pricing" className="font-medium underline">
                Settings → Pricing & Rates
              </Link>{" "}
              to unlock siding estimates.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const sidingAreaNum = parseFloat(sidingArea) || 0;
  const soffitAreaNum = parseFloat(soffitArea) || 0;
  const fasciaLengthNum = parseFloat(fasciaLength) || 0;

  const materialRate = material === "vinyl" 
    ? settings.siding_price_per_sqft_vinyl 
    : settings.siding_price_per_sqft_fiber;

  const sidingTotal = sidingAreaNum * materialRate;
  const soffitTotal = soffitAreaNum * settings.soffit_price_per_sqft;
  const fasciaTotal = fasciaLengthNum * settings.fascia_price_per_ft;
  const subtotalCost = sidingTotal + soffitTotal + fasciaTotal;

  const overheadCost = subtotalCost * (settings.overhead_percent / 100);
  const costWithOverhead = subtotalCost + overheadCost;
  const profitAmount = costWithOverhead * (settings.profit_margin_percent / 100);
  const finalCustomerPrice = costWithOverhead + profitAmount;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle>Siding Measurements</CardTitle>
          <CardDescription>Auto-measure or enter dimensions manually</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Auto-Measure Button */}
          <div className="pb-4 border-b">
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={handleAutoMeasureFromMap}
              disabled={!buildingData}
            >
              <MapPin className="w-4 h-4" />
              Auto-Measure From Map
            </Button>
          </div>
          <div className="space-y-2">
            <Label htmlFor="material">Material Type</Label>
            <Select value={material} onValueChange={(value: "vinyl" | "fiber") => setMaterial(value)}>
              <SelectTrigger id="material">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vinyl">Vinyl Siding</SelectItem>
                <SelectItem value="fiber">Fiber Cement / Hardie</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sidingArea">Total Siding Area (ft²)</Label>
            <Input
              id="sidingArea"
              type="number"
              placeholder="0"
              value={sidingArea}
              disabled={measuring}
              onChange={(e) => setSidingArea(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="soffitArea">Soffit Area (ft²)</Label>
            <Input
              id="soffitArea"
              type="number"
              placeholder="0"
              value={soffitArea}
              disabled={measuring}
              onChange={(e) => setSoffitArea(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fasciaLength">Fascia Length (ft)</Label>
            <Input
              id="fasciaLength"
              type="number"
              placeholder="0"
              value={fasciaLength}
              disabled={measuring}
              onChange={(e) => setFasciaLength(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Estimate Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Siding Quick Estimate
          </CardTitle>
          <CardDescription>Ballpark pricing based on your rates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Material & Labor Costs */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground">Material & Labor Costs</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {material === "vinyl" ? "Vinyl" : "Fiber Cement"} Siding ({sidingAreaNum.toFixed(0)} ft²)
                </span>
                <span className="font-medium">${sidingTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Soffit ({soffitAreaNum.toFixed(0)} ft²)</span>
                <span className="font-medium">${soffitTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Fascia ({fasciaLengthNum.toFixed(0)} ft)</span>
                <span className="font-medium">${fasciaTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="font-medium">Subtotal</span>
                <span className="font-semibold">${subtotalCost.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Business Costs & Profit */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground">Business Costs & Profit</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Overhead ({settings.overhead_percent}%)</span>
                <span className="font-medium">${overheadCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Profit Margin ({settings.profit_margin_percent}%)</span>
                <span className="font-medium">${profitAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Final Customer Price */}
          <div className="pt-4 border-t">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold">Customer Price</span>
              <span className="text-2xl font-bold text-primary">${finalCustomerPrice.toFixed(2)}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Includes ${profitAmount.toFixed(2)} profit
            </p>
          </div>

          <p className="text-xs text-muted-foreground pt-2 border-t">
            Adjust your rates in{" "}
            <Link to="/settings?tab=pricing" className="underline">
              Settings
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
